import {
    CommercialAccessories,
    CommercialCell,
    CommercialPriceRangeCell,
    DBCommercialProduct,
} from '@Commercial/Products/Data/DataSource/API/Entity/Product'
import { Dispatch, SetStateAction } from 'react'
import { DBCommercialProposal } from '../Data/DataSource/API/Entity/Proposal'
import { IFormInputs } from '../Hooks/useNewProposal/useNewProposal.types'
import { calculateSaleValue } from './calculateBv'
import { removeLeadingZeros } from './removeLeadingZeros'

const DEBUG_STORAGE_KEY = 'commercialProposalDebug'

const isCommercialProposalDebugEnabled = () => {
    if (typeof window === 'undefined') {
        return false
    }

    const globalWindow = window as Window & {
        __COMMERCIAL_PROPOSAL_DEBUG__?: boolean
    }

    return (
        globalWindow.__COMMERCIAL_PROPOSAL_DEBUG__ === true ||
        window.localStorage.getItem(DEBUG_STORAGE_KEY) === '1' ||
        window.localStorage.getItem(DEBUG_STORAGE_KEY) === 'true'
    )
}

const debugLog = (...args: unknown[]) => {
    if (isCommercialProposalDebugEnabled()) {
        console.debug(...args)
    }
}

export const formCalculate =
    (setState: Dispatch<SetStateAction<IFormInputs>>) =>
    (value: DBCommercialProposal, { name }: { name: any }) => {
        for (let index = 0; index < value.products.length; index++) {
            if (value.products[index].isActive === undefined) {
                value.products[index].isActive = true
            }
            if (value.products[index].unitCost === undefined) {
                value.products[index].unitCost = 0
            }
            if (value.products[index].quantity === undefined) {
                value.products[index].quantity = 0
            }

            value.products[index].unitCost = removeLeadingZeros(
                value.products[index].unitCost
            )
            value.products[index].freight =
                removeLeadingZeros(value.products[index].freight) || 0
            const isCord = value.products[index].productType === 'dynamic'
            const isRoller = value.products[index].productType === 'rollerClip'
            const isGift = value.products[index].productType === 'gift'
            const isJoker = value.products[index].name === 'Outros'
            if (isCord || isRoller || isGift) {
                if (isCord) {
                    value.products[index].unitCost = calculateCost(
                        value.products[index].size,
                        value.products[index].quantity,
                        value.products[index].cordType
                    )
                    value.products[index].unitCost =
                        calculateAccessories(value.products[index]) +
                        value.products[index].unitCost
                }
                if (
                    (isRoller || isGift) &&
                    !value.products[index].markupTable
                ) {
                    value.products[index].markupTable =
                        value.products[index].markups?.[0]
                }

                const { markup, minMarkup, maxMarkup } = getCordMarkup(
                    value.products[index],
                    value.products[index].quantity
                )

                if (
                    value.products[index].maxMarkup !== maxMarkup ||
                    value.products[index].minMarkup !== minMarkup
                ) {
                    value.products[index].maxMarkup = maxMarkup
                    value.products[index].minMarkup = minMarkup
                    value.products[index].markup = markup
                }

                if (!value?.products[index]?.markup) {
                    value.products[index].markup = markup
                }
            }
            if (value?.bvPercentage) {
                value.products[index].unitCostTotal = calculateSaleValue(
                    Number(value.products[index].unitCostTotal),
                    Number(value.bvPercentage) / 100
                )
            }
            if (value.products?.[index] && !value?.products[index]?.markup) {
                value.products[index].markup = 100
            }

            let unitPageCost = 0
            if (isRoller) {
                const { totalPagesCost, pages } = calculateRollerPageCost(
                    value.products[index]
                )
                value.products[index].pages = pages
                value.products[index].totalPagePrice = totalPagesCost
                unitPageCost =
                    value.products[index].totalPagePrice /
                    Number(value.products[index].quantity)
                value.products[index].unitPageCost = unitPageCost
            }
            if (isGift) {
                const { unitCost: cost } = calculateCostForGift(
                    value.products[index]
                )
                debugLog('DEBUG isGift:', cost)
                value.products[index].unitCost = cost
            }
            if (isJoker) {
                const { unitCost: cost } = calculateCostForJoker(
                    value.products[index]
                )
                debugLog('DEBUG isJoker:', cost)
                value.products[index].unitCostTotal = cost
            }

            // Calculate base unit cost
            const baseUnitCost = Number(
                String(
                    isJoker
                        ? value?.products[index]?.unitCostTotal
                        : value?.products[index]?.unitCost
                ).replace(',', '.')
            )

            // Calculate tax amount based on tax percentage
            // Garante que valores com vírgula sejam convertidos corretamente
            const taxPercentageStr = String(
                value.products?.[index]?.taxPercentage || 0
            ).replace(',', '.')
            const taxPercentage = Number(taxPercentageStr)
            const taxAmount = (baseUnitCost * taxPercentage) / 100

            // Include tax in unit cost
            const unitCostWithTax = baseUnitCost + taxAmount

            const unitFreight = calculateSaleValue(
                (Number(removeLeadingZeros(value.products?.[index]?.freight)) ||
                    0) /
                    (Number(value.products?.[index]?.quantity) || 0) +
                    unitCostWithTax +
                    Number(unitPageCost || 0),
                Number(value.bvPercentage) || 0
            )

            // For unitFreightWithoutBV, we also need to apply the tax percentage
            const unitFreightWithoutBV = calculateSaleValue(
                (Number(removeLeadingZeros(value.products?.[index]?.freight)) ||
                    0) /
                    (Number(value.products?.[index]?.quantity) || 0) +
                    unitCostWithTax + // Use the tax-adjusted unit cost here too
                    Number(unitPageCost || 0),
                0
            )

            const unitPrice =
                (Number(value?.products[index]?.markup) / 100 + 1) * unitFreight

            value.products[index].unitCostTotal =
                Number(unitFreight) ||
                Number(
                    String(value?.products[index]?.unitCost).replace(',', '.')
                ) /
                    100 +
                    Number(
                        String(value?.products[index]?.unitCost).replace(
                            ',',
                            '.'
                        )
                    )

            value.products[index].unitCostTotalFull =
                Number(unitFreightWithoutBV) ||
                Number(
                    String(value?.products[index]?.unitCost).replace(',', '.')
                ) /
                    100 +
                    Number(
                        String(value?.products[index]?.unitCost).replace(
                            ',',
                            '.'
                        )
                    )

            const unitDiscount =
                (unitPrice * Number(value?.discount || 0)) / 100
            value.products[index].unitDiscount = unitDiscount
            value.products[index].unitPriceTotal = Number(
                unitPrice - unitDiscount || 0
            )

            value.products[index].totalCost =
                Number(unitPrice - unitDiscount) *
                Number(value?.products[index]?.quantity)

            debugLog('DEBUG totalCost:', value.products[index].totalCost)

            value.products[index].totalPriceFull =
                Number(unitPrice - unitDiscount) *
                Number(value?.products[index]?.quantity)
            const unitPriceWithoutBV =
                Number(
                    (removeLeadingZeros(value.products?.[index]?.freight) ||
                        0) / (value.products?.[index]?.quantity || 0)
                ) +
                Number(
                    String(value?.products[index]?.unitCost).replace(',', '.')
                ) +
                Number(unitPageCost || 0)

            const unitPriceWithMarkup =
                (Number(value?.products[index]?.markup) / 100 + 1) *
                (unitPriceWithoutBV - unitDiscount)

            const errorMarginQuantity = Number(
                value?.products[index]?.errorMarginQuantity || 0
            )
            const hasErrorMargin =
                value?.products[index]?.hasErrorMargin === true &&
                errorMarginQuantity > 0

            const quantityWithMargin = hasErrorMargin
                ? Number(value?.products[index]?.quantity) + errorMarginQuantity
                : Number(value?.products[index]?.quantity)

            // value.products[index].totalCost =
            //     unitPriceWithoutBV * quantityWithMargin

            value.products[index].totalCost =
                (isJoker
                    ? value.products[index].unitCostTotalFull
                    : value.products[index].unitCostTotal) *
                value?.products[index]?.quantity

            debugLog(
                'DEBUG unitcost:',
                isJoker
                    ? value.products[index].unitCostTotalFull
                    : value.products[index].unitCostTotal
            )
            debugLog('DEBUG quantity:', value?.products[index]?.quantity)
            debugLog('DEBUG totalCost:', value.products[index].totalCost)

            value.products[index].totalPrice =
                unitPriceWithMarkup * quantityWithMargin

            if (index === value.products.length - 1) {
                return setState((prev) => {
                    if (name) {
                        prev = value
                    }
                    return prev
                })
            }
        }
    }

const calculateAccessories = (product: any) => {
    const cordAccessoriesUnitCost = product?.cordAccessories?.reduce(
        (acc, { value }) => acc + Number(value?.value),
        0
    )
    return cordAccessoriesUnitCost
}

export const calculateCost = (
    size: CommercialCell,
    quantity: number = 1,
    cordType: any
) => {
    try {
        if (
            size &&
            cordType &&
            cordType.table &&
            cordType.table.rows &&
            cordType.table.columns
        ) {
            let getSizeRow = cordType.table.rows.find(
                (row: CommercialCell[]) => row[0]?.value === size?.value
            )
            const [__, ...columns] = cordType.table.columns

            getSizeRow = getSizeRow || []

            const [_, ...quantityPrice] = getSizeRow

            const index = columns.findIndex(({ value }: CommercialCell) => {
                const [min, max = Infinity] = value
                    .split('a')
                    .map((item) => String(item).replace(/\D/g, ''))
                    .map(Number)
                return (
                    Number(quantity) >= Number(min) &&
                    Number(quantity) <= Number(max)
                )
            })
            const unitCost = Number(
                String(
                    quantityPrice[index]?.value || quantityPrice[0]?.value || 0
                ).replace(',', '.')
            )
            return (
                unitCost ||
                Number(String(quantityPrice[0]?.value || 0).replace(',', '.'))
            )
        }
        return 0
    } catch (e) {
        debugLog(e)
        return 0
    }
}
export const calculateCostForGift = (product: DBCommercialProduct) => {
    try {
        const unitCostExtra = calculateUnitCostExtra(product)
        const quantityPrice = product?.priceRange?.rows[0]
        let multiplier = product?.multiplier || 1
        debugLog('DEBUG calculateCostForGift:', multiplier, product?.multiplier)
        if (!quantityPrice) {
            return {
                unitCost: unitCostExtra,
                isFixed: false,
            }
        }
        const quantity = Number(product?.quantity || 1)
        const errorMarginQuantity = Number(product?.errorMarginQuantity || 0)
        const hasErrorMargin =
            product?.hasErrorMargin === true && errorMarginQuantity > 0


        // Calculamos o custo unitário para a quantidade total (quantidade + margem de erro)
        const totalQuantity = hasErrorMargin
            ? quantity + errorMarginQuantity
            : quantity

        // Encontramos o índice baseado na quantidade total (incluindo margem)
        const index = findQuantityIndex({ ...product, quantity: quantity })
        const selectedPriceRange = quantityPrice[index]

        if (index !== -1) {
            // Obtemos o custo unitário baseado na tabela de preços
            const unitCost = calculateUnitCost(
                selectedPriceRange,
                totalQuantity
            )

            // Se tiver margem de erro, diluímos o custo das unidades extras
            if (hasErrorMargin) {

                // Calculamos o custo unitário base (incluindo multiplicador e custo extra)
                const baseUnitCost = unitCost * (multiplier||1) + unitCostExtra

                // O custo total considera TODAS as unidades (incluindo a margem)
                const totalCost = baseUnitCost * totalQuantity

                // Distribuímos esse custo total apenas entre a quantidade solicitada
                return {
                    unitCost: totalCost / quantity,
                    isFixed: selectedPriceRange?.isFixed,
                }
            }

            // Sem margem de erro, retorna o cálculo normal
            return {
                unitCost: unitCost * (multiplier||1) + unitCostExtra,
                isFixed: selectedPriceRange?.isFixed,
            }
        }

        return {
            unitCost:
                calculateUnitCost(quantityPrice[0], quantity) * (multiplier||1) +
                unitCostExtra,
            isFixed: quantityPrice[0]?.isFixed,
        }
    } catch (e) {
        debugLog(e)
        return {
            unitCost: 0,
            isFixed: false,
        }
    }
}

export const calculateCostForJoker = (product: DBCommercialProduct) => {
    try {
        const quantity = Number(product?.quantity || 1)
        const errorMarginQuantity = Number(product?.errorMarginQuantity || 0)
        const hasErrorMargin =
            product?.hasErrorMargin === true && errorMarginQuantity > 0

        debugLog('DEBUG isJoker calculateCostForGift:', {
            product,
            productName: product?.name,
            hasErrorMargin: product?.hasErrorMargin,
            errorMarginQuantity,
            quantity,
            totalQuantity: hasErrorMargin
                ? quantity + errorMarginQuantity
                : quantity,
            condition: hasErrorMargin,
        })

        // Calculamos o custo unitário para a quantidade total (quantidade + margem de erro)
        const totalQuantity = hasErrorMargin
            ? quantity + errorMarginQuantity
            : quantity

        // Encontramos o índice baseado na quantidade total (incluindo margem)
        const selectedPriceRange = {
            value: product.unitCost,
            isFixed: false,
        }
        debugLog('DEBUG isJoker selectedPriceRange:', totalQuantity)

        // Obtemos o custo unitário baseado na tabela de preços
        const unitCost = calculateUnitCost(selectedPriceRange, totalQuantity)

        // Se tiver margem de erro, diluímos o custo das unidades extras
        if (hasErrorMargin) {
            // Calculamos o custo unitário base (incluindo multiplicador e custo extra)
            const baseUnitCost = unitCost

            // O custo total considera TODAS as unidades (incluindo a margem)
            const totalCost = baseUnitCost * totalQuantity

            // Distribuímos esse custo total apenas entre a quantidade solicitada
            return {
                unitCost: totalCost / quantity,
            }
        }

        // Sem margem de erro, retorna o cálculo normal
        return {
            unitCost: unitCost,
        }
    } catch (e) {
        debugLog(e)
        return {
            unitCost: 0,
            isFixed: false,
        }
    }
}

export const calculateUnitCostExtra = (product: DBCommercialProduct) => {
    const additionalProductUnitCost =
        Number(String(product?.additionalProductUnitCost)?.replace(',', '.')) ||
        0
    const accessoriesCost = calculateAccessoriesCost(product?.giftAccessories)

    return additionalProductUnitCost + accessoriesCost
}

export const calculateAccessoriesCost = (
    accessories?: CommercialAccessories[]
) => {
    if (!accessories) {
        return 0
    }

    return accessories.reduce((acc, { value }) => acc + Number(value?.value), 0)
}

export const findQuantityIndex = (product: DBCommercialProduct) => {
    const columns = product?.priceRange?.columns
    const quantity = product?.quantity
    const quantityPrice = product?.priceRange?.rows[0]

    if (!columns || !quantityPrice) {
        return -1
    }

    return columns.findIndex(({ value }) => {
        const range = value
            .split('a')
            .map((item) => String(item).replace(/\D/g, ''))
        const min = Number(range[0])
        const max = Number(range[1]) || Infinity
        return Number(quantity) >= min && Number(quantity) <= max
    })
}

export const calculateUnitCost = (
    priceRange: CommercialPriceRangeCell,
    quantity: number
) => {
    const priceValue = priceRange.value
    const unitCost = Number(String(priceValue).replace(',', '.'))

    if (priceRange.isFixed) {
        return unitCost / quantity
    }

    return unitCost
}
const calculateRollerPageCost = (product: any) => {
    const { quantity, pagePrice, quantityPerPage } = product
    const pages = Math.ceil(quantity / quantityPerPage)
    return { totalPagesCost: pagePrice * pages, pages: pages }
}

const getCordMarkup = (product: any, quantity = 1) => {
    if (product?.markupTable) {
        const { columns, rows } = product?.markupTable || [[], []]
        const index = columns.findIndex(({ value }: CommercialCell) => {
            const range = value
                .split('a')
                .map((item) => String(item).replace(/\D/g, ''))
            const min = Number(range[0])
            const max = Number(range[1]) || Infinity
            const isQuantityInRange =
                Number(quantity) >= min && Number(quantity) <= max
            return isQuantityInRange
        })

        if (index !== -1) {
            return {
                markup: Number(String(rows[0][index]?.markup)),
                maxMarkup: Number(String(rows[0][index]?.maxMarkup)),
                minMarkup: Number(String(rows[0][index]?.minMarkup)),
            }
        } else {
            return {
                markup: Number(String(rows[0][0]?.markup)),
                maxMarkup: Number(String(rows[0][0]?.maxMarkup)),
                minMarkup: Number(String(rows[0][0]?.minMarkup)),
            }
        }
    }
    return {
        markup: product.markup || 0,
        maxMarkup: product.maxMarkup || 0,
        minMarkup: product.minMarkup || 0,
    }
}
